import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { initFeatureFlags } from '@plentiau/fe-feature-flag-client';
import { Scope } from '@plentiau/fe-feature-flag-client/build/config';
import { FlagsByProjects } from '@plentiau/fe-feature-flag-client/build/model/flag';

import { environment } from 'environments/environment';
import { FFScope, getFeatureName } from '@brokerportal/common/enums/feature-flag';
import { Dictionary } from '../models';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {
    /**
     * Cache duration in seconds.
     */
    private cacheDuration = 60;
    private readonly localCache = new Map<string, { value: Dictionary<boolean>; expiry: number }>();

    constructor() {}

    /**
     * @deprecated Consider using `isEnabled('PlCarNewStatusDisplay')`.
     */
    PlCarNewStatusDisplay() {
        return environment.features.PlCarNewStatusDisplay;
    }

    /**
     * @deprecated Consider using `isEnabled('EnableEditBorrowerInfo')`.
     */
    EnableEditBorrowerInfo() {
        return environment.features.EnableEditBorrowerInfo;
    }

    /**
     * @deprecated Consider using `isEnabled('EnableIdentityAsLoanCondition')`.
     */
    EnableIdentityAsLoanCondition() {
        return environment.features.EnableIdentityAsLoanCondition;
    }

    private init(subscope = undefined) {
        const scope = this.getScope(subscope);
        return new Observable<FlagsByProjects | null>(observer => {
            initFeatureFlags(
                [FFScope.BrokerPortal, FFScope.Venus],
                scope as Scope,
                undefined,
                undefined,
                error => {
                    console.warn('Feature Flag init failed: ', error);
                    observer.error(error);
                },
                flags => {
                    console.debug('Feature Flags init successfully.');
                    observer.next(flags);
                    observer.complete();
                }
            );
        });
    }

    isEnabled(
        featureCode: string,
        opts: { overrideWithLocal?: boolean; useCache?: boolean } = { overrideWithLocal: true, useCache: true }
    ) {
        return this.isEnabledInSubscope(featureCode, null, opts);
    }

    isEnabledInSubscope(
        featureCode: string,
        subscope: string | undefined | null,
        { overrideWithLocal = true, useCache = true }: { overrideWithLocal?: boolean; useCache?: boolean } = {}
    ) {
        return this.get(useCache, subscope).pipe(
            map(flags => {
                const featureName = getFeatureName(featureCode);
                const scopedFeatureName = featureName + (subscope ? `-${subscope}` : '');
                const isEnabledLocal = environment.features[scopedFeatureName];
                if (overrideWithLocal !== false && typeof isEnabledLocal === 'boolean') return isEnabledLocal;
                return typeof flags[featureCode] === 'boolean' ? flags[featureCode] : !!isEnabledLocal;
            }),
            catchError((err, caught) => {
                console.warn(err, caught);
                return of(false);
            })
        );
    }

    private get(useCache = true, subscope?: string) {
        const scope = this.getScope(subscope);
        const cacheValue = this.getCache(scope);
        if (!cacheValue || useCache === false) {
            return this.init(subscope).pipe(
                catchError((err, caught) => {
                    console.warn('Error occurred while initializing Feature Flags: ', err);
                    return throwError(err);
                }),
                map((flagsByProjects: FlagsByProjects | null) => {
                    if (!flagsByProjects) return null;

                    const brokerPortalFlags = (flagsByProjects[FFScope.BrokerPortal] || []).reduce<Dictionary<boolean>>(
                        (acc, cur) => ({ ...acc, [cur.code]: cur.isEnabled }),
                        {}
                    );

                    const venusFlags = (flagsByProjects[FFScope.Venus] || []).reduce<Dictionary<boolean>>(
                        (acc, cur) => ({ ...acc, [cur.code]: cur.isEnabled }),
                        {}
                    );

                    return { ...brokerPortalFlags, ...venusFlags };
                }),
                tap(dict => (dict ? this.setCache(scope, dict) : void 0))
            );
        }

        return of(cacheValue);
    }

    private getScope(subscope?: string) {
        let scope = environment.featureScope;
        if (subscope) {
            scope += `-${subscope}`;
        }
        return scope;
    }

    private getCacheKey(scope: string) {
        return `features-${FFScope.BrokerPortal}-${scope}`;
    }

    private getCache(scope: string): Dictionary<boolean> | null {
        const cacheKey = this.getCacheKey(scope);
        const cache = this.localCache.get(cacheKey);
        const expiryTime = cache?.expiry;
        const isExpired = typeof expiryTime === 'number' ? new Date().getTime() >= expiryTime : true;
        return isExpired ? null : cache?.value;
    }

    private setCache(scope: string, data: Dictionary<boolean>) {
        const cacheKey = this.getCacheKey(scope);
        this.localCache.set(cacheKey, { value: data, expiry: Date.now() + this.cacheDuration * 1000 });
    }
}

export const isEnabledOverwriteFunc = (project: string, flagCode: string): boolean => {
    if (project === FFScope.BrokerPortal) {
        return !!environment.features[getFeatureName(flagCode)];
    }

    return false;
};
